import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { Button } from "../components/ui/button";
import { PageLayout } from "../components/PageLayout";

function Profile(
  name: string,
  title: string | null = null,
  imageUrl: string = "/placeholder.svg?height=400&width=400",
) {
  return (
    <div className="space-y-4">
      <div className="aspect-square overflow-hidden rounded-lg bg-muted">
        <img
          src={imageUrl}
          alt={`${name}'s profile`}
          width={400}
          height={400}
          className="object-cover w-full h-full"
        />
      </div>
      <div>
        <h3 className="font-bold text-lg">{name}</h3>
        {title && <p className="text-muted-foreground">{title}</p>}
      </div>
    </div>
  );
}

function Technology(
  name: string,
  description: string,
  imageUrl: string = "/placeholder.svg?height=300&width=300",
) {
  return (
    <div className="bg-muted/30 rounded-lg overflow-hidden">
      <div className="aspect-square relative">
        <img
          src={`${imageUrl}?height=300&width=300`}
          alt={name}
          width={300}
          height={300}
          className="object-cover"
        />
      </div>
      <div className="p-4">
        <h3 className="font-bold mb-2">{name}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <PageLayout>
      <div className="container max-w-5xl py-12 space-y-24">
        {/* Hero Section */}
        <section className="space-y-8">
          <div className="space-y-4 text-center">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Our Company
            </p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              About
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Canvas Editor is an innovative document creation platform. Our
              mission is to transform how people convert images to editable
              documents with powerful AI technology.
            </p>
          </div>

          <div className="aspect-[16/9] overflow-hidden rounded-lg">
            <img
              src="/about/group-photo-hero.jpg"
              alt="httpsunsplash.comphotosgroup-of-people-sitting-on-bench-near-trees-duting-daytime-Q_Sei-TqSlc"
              width={1200}
              height={600}
              className="object-cover w-full"
            />
          </div>
        </section>

        {/* Our Vision Section */}
        <section className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
              Our vision for the future of document creation
            </h2>
            <p className="text-muted-foreground">
              Our mission is to ensure that document creation and editing
              becomes more accessible and efficient for everyone—transforming
              how people work with visual information and text.
            </p>
            <p className="text-muted-foreground">
              We believe that by removing barriers between visual content and
              editable documents, we can help people focus on what matters most:
              their ideas and information.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link to="/editor">
                <Button className="gap-2">
                  Try the Editor
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/templates">
                <Button variant="outline">Browse Templates</Button>
              </Link>
            </div>
          </div>
          <div className="aspect-square overflow-hidden rounded-lg bg-muted">
            <img
              src="/about/vision.jpg"
              alt="https://unsplash.com/photos/three-men-sitting-while-using-laptops-and-watching-man-beside-whiteboard-wD1LRb9OeEo"
              width={600}
              height={600}
              className="object-cover w-full h-full"
            />
            <p className="text-xs text-muted-foreground text-center mt-2">
              Illustration: Visualization of document transformation
            </p>
          </div>
        </section>

        {/* Our Approach Section */}
        <section className="space-y-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tight mb-6">
              We are building intelligent document tools that enhance creativity
              and productivity, while ensuring accessibility for users of all
              technical backgrounds.
            </h2>
          </div>

          <div className="aspect-[16/9] overflow-hidden rounded-lg">
            <img
              src="/about/experiences.png"
              alt="User experience"
              width={1200}
              height={600}
              className="object-cover w-full"
            />
          </div>
        </section>

        {/* Our Technology Section */}
        <section className="space-y-8">
          <h2 className="text-3xl font-bold tracking-tight">Our technology</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Technology(
              "AI Image Recognition",
              "Advanced computer vision algorithms that accurately identify text, shapes, and structures in images.",
              "/about/ocr.png",
            )}
            {Technology(
              "Smart Document Processing",
              "Intelligent systems that convert visual elements into editable, structured documents.",
              "/about/smart-processing.png",
            )}
            {Technology(
              "Intuitive Editing Tools",
              "User-friendly interface designed for seamless editing and customization of converted documents.",
              "/about/editing-tools.png",
            )}
            {Technology(
              "Multi-format Export",
              "Flexible export options that allow users to save their work in various file formats.",
              "/about/export.png",
            )}
          </div>
        </section>

        {/* Team Section */}
        <section className="space-y-8">
          <h2 className="text-3xl font-bold tracking-tight">Our team</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Profile(
              "Ackkerman",
              "Developer & Designer",
              "https://avatars.githubusercontent.com/u/54477647?s=400&u=7b5961f740529966420fa2b6caad08e1a91beb2e&v=4",
            )}
          </div>
        </section>

        {/* Credits & Licenses Section */}
        <section className="space-y-8">
          <h2 className="text-3xl font-bold tracking-tight">
            Credits & Licenses
          </h2>
          <p className="text-muted-foreground">
            This project uses open-source libraries and assets licensed under
            their respective terms. All trademarks are the property of their
            respective owners.
          </p>
        </section>

        {/* CTA Section */}
        <section className="bg-muted/30 rounded-lg p-8 md:p-12">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
              Ready to transform your documents?
            </h2>
            <p className="text-xl text-muted-foreground">
              Join thousands of users who are already enhancing their
              productivity with Canvas Editor.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <Link to="/capture">
                <Button size="lg" className="gap-2">
                  Get Started
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/templates">
                <Button size="lg" variant="outline">
                  Explore Templates
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
